import api from "@/lib/axiosConfig.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { FileUpload } from "./image";
import { z } from "zod";
import { PagingParameters } from "./dtos";

import { getActiveRegion } from "@/config/regions";

const CONV_URL = import.meta.env.VITE_CONVERTISSEUR_URL;

export type Sorting = {
  orderBy?: string;
  orderDirection?: "asc" | "desc";
}

export const fileSchema = z.object({
  id: z.string().uuid(),
  docId: z.string().uuid(),
  title: z.string().min(1),
  description: z.string().optional(),
  sourceName: z.string().optional(),
  publicUrl: z.string().url().optional(),
  tag: z.string().optional(),
  createdAt: z.string(),
});

export type AppFile = z.infer<typeof fileSchema>;

export const baseRoute = "back/api/file";

async function getFileByTag(tag: string, instanceId: string) {
  const { data } = await api.get(`back/api/file/tag/${tag}/${instanceId}`);
  return data;
}

export const useGetFileByTag = (tag: string, instanceId: string) => {
  return useQuery({
    queryKey: ["file", tag, instanceId],
    queryFn: () => getFileByTag(tag, instanceId),
    enabled: !!tag && !!instanceId,
  });
};

export async function getFileByDocId(docId: string, sorting?: Sorting, pagination?: PagingParameters) {
  const params: Sorting = {};
  if (sorting) {
    params["orderBy"] = sorting.orderBy;
    params["orderDirection"] = sorting.orderDirection;
  }
  if (!pagination) {
    pagination = { page: 0, pageSize: 10 };
  }
  const { data } = await api.get(`back/api/file/doc/${docId}`, {
    params: {
      ...params,
      ...pagination,
    },
  });
  return data;
}

export const useGetFileByDocId = (docId: string, sorting?: Sorting, pagination?: PagingParameters) => {
  return useQuery({
    queryKey: ["file", docId, sorting, pagination],
    queryFn: () => getFileByDocId(docId, sorting, pagination),
    enabled: !!docId,
  });
};

export async function getDocShareLink(docId: string) {
  const data = await api.get(`back/api/file/doc/share/${docId}`);
  return data;
}

export const useGetDocShareLink = (docId: string) => {
  return useQuery({
    queryKey: ["file", { fileId: docId }],
    queryFn: () => getDocShareLink(docId),
    enabled: !!docId,
  });
};

async function deleteFile(id: string) {
  const { data } = await api.delete(`back/api/file/delete/${id}`);
  return data;
}

export const useDeleteFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFile,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["file", id] });
    },
  });
};
export async function updateFileById(id: string, data: Partial<AppFile>) {
  const response = await api.patch(`back/api/file/${id}`, data);
  return response.data;
}
export async function uploadDocumentForApp(
  file: File,
  s3bucket: string,
  data: FileUpload,
  instanceId: string,
): Promise<any> {
  const region = getActiveRegion();
  const apiUrl = region.baseUrl;

  const formData = new FormData();
  formData.append("doc", file);
  formData.append("S3_BUCKET", s3bucket);
  formData.append("sourceName", data.sourceName);
  formData.append("title", data.title);
  formData.append("tag", data.tag);
  formData.append("description", data.description ?? "");
  formData.append("docId", data.docId);
  formData.append("mimetype", data.mimetype);
  formData.append("API_URL", apiUrl + "back/api/file/create/app");
  formData.append("instanceId", instanceId);
  formData.append("provider", region.storageProvider);
  formData.append("region", region.code);

  const response = await axios.post(CONV_URL + "uploadV3/document", formData);
  return response.data;

}

interface UploadDocumentParams {
  file: File;
  s3bucket: string;
  data: FileUpload;
  instanceId: string;
}

export const useUploadDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: UploadDocumentParams) =>
      uploadDocumentForApp(
        params.file,
        params.s3bucket,
        params.data,
        params.instanceId,
      ),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["file", v.data.docId] });
    },
  });
};

const _updateFileSchema = fileSchema.omit({ createdAt: true });
type UpdateFile = z.infer<typeof _updateFileSchema>;

export const updateFile = async ({ id, ...file }: UpdateFile) => {
  await api.patch(`${baseRoute}/${id}`, file);
};

export const useUpdateFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateFile,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["file", v.id] });
    },
  });
};
